"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getSDEValues = exports.createOrSetResult = exports.setResult = exports.getResult = exports.hasResult = exports.buildPopulationGroupRelevanceMap = exports.buildPopulationRelevanceMap = exports.buildPopulationRelevanceForAllEpisodes = exports.doesResultPass = exports.findResultForStatementClause = exports.setFinalResults = exports.prettyResult = exports.buildStatementAndClauseResults = exports.getStatementResult = exports.markStatementRelevant = exports.buildStatementRelevanceMap = void 0;
var ClauseResultsHelpers = __importStar(require("./ClauseResultsHelpers"));
var MeasureBundleHelpers = __importStar(require("../helpers/MeasureBundleHelpers"));
var ELMDependencyHelper = __importStar(require("../helpers/elm/ELMDependencyHelpers"));
var cql_execution_1 = require("cql-execution");
var moment_1 = __importDefault(require("moment"));
var Enums_1 = require("../types/Enums");
/**
 * Contains helpers that generate useful data for coverage and highlighing.
 */
/**
 * Builds the initial list of StatementResult objects. This initializes the objects with ith statement relevance info.
 * This list gets added to the DetailedPopulationGroupResult for the given population group.
 *
 * The statement relevance indicates which define statements were actually relevant to a population inclusion
 * consideration. This makes use of the 'population relevance' info. The values for statement relevance differ from the
 * `population relevance` because we also need to track statements that are not used for any population calculation.
 * Therefore the values are a string/enum that is one of the following: 'NA', 'TRUE', 'FALSE'. Here is what they mean:
 *
 * 'NA' - Not applicable. This statement is not relevant to any population calculation in this population_set. Common
 *   for unused library statements or statements only used for other population sets.
 *
 * 'FALSE' - This statement is not relevant to any of this patient's population inclusion calculations.
 *
 * 'TRUE' - This statement is relevant for one or more of the population inclusion calculations.
 *
 * This function relies heavily on the statement dependency map built in ELMDependencyHelper to recursively determine
 * which statements are used in the relevant population statements. It also uses the 'population_relevance' map to
 * determine the relevance of the population defining statement and its dependent statements.
 *
 * @public
 * @param {fhir4.Measure} measure - The measure.
 * @param {PopulationResult[]} populationRelevanceSet - The population relevance results, used at the starting point.
 * @param {string} mainLibraryId - The identifier of the main library.
 * @param {ELM[]} elmLibraries - All ELM libraries for the measure.
 * @param {fhir4.MeasureGroup} populationGroup - The population group being calculated.
 * @param {boolean} calculateSDEs - Wether or not to treat SDEs as calculed/relevant or not.
 * @returns {StatementResult[]} The StatementResults list for each statement with it its relevance status populated.
 */
function buildStatementRelevanceMap(measure, populationRelevanceSet, mainLibraryId, elmLibraries, populationGroup, calculateSDEs) {
    var _a, _b;
    // build statement results defaulting to not applicable (NA)
    var statementResults = [];
    elmLibraries.forEach(function (elmLibrary) {
        var _a;
        (_a = elmLibrary.library.statements) === null || _a === void 0 ? void 0 : _a.def.forEach(function (statement) {
            var statementResult = {
                libraryName: elmLibrary.library.identifier.id,
                statementName: statement.name,
                localId: statement.localId,
                final: Enums_1.FinalResult.NA,
                relevance: Enums_1.Relevance.NA
            };
            statementResults.push(statementResult);
        });
    });
    // build statement dependency maps to use for marking relevant statements
    // TODO: Move this out to only happen once per measure to improve performance.
    var statementDependencies = ELMDependencyHelper.buildStatementDependencyMaps(elmLibraries);
    // Calculate SDEs if specified
    if (calculateSDEs && measure.supplementalData) {
        measure.supplementalData.forEach(function (sde) {
            var _a;
            if ((_a = sde.criteria) === null || _a === void 0 ? void 0 : _a.expression) {
                markStatementRelevant(elmLibraries, statementDependencies, statementResults, mainLibraryId, sde.criteria.expression, true);
            }
        });
    }
    // if we have stratifications, mark all strata statements relevant
    (_a = populationGroup.stratifier) === null || _a === void 0 ? void 0 : _a.forEach(function (strata) {
        var _a;
        if ((_a = strata.criteria) === null || _a === void 0 ? void 0 : _a.expression) {
            markStatementRelevant(elmLibraries, statementDependencies, statementResults, mainLibraryId, strata.criteria.expression, true);
        }
    });
    // Iterate over all populations in this group and mark their statements relevant.
    (_b = populationGroup.population) === null || _b === void 0 ? void 0 : _b.forEach(function (population) {
        var popType = MeasureBundleHelpers.codeableConceptToPopulationType(population.code);
        if (popType) {
            // If the population is values, that means we need to mark relevance for the OBSERVs
            var relevance = getResult(popType, populationRelevanceSet);
            var relevantStatement = population.criteria.expression;
            if (relevantStatement) {
                markStatementRelevant(elmLibraries, statementDependencies, statementResults, mainLibraryId, relevantStatement, relevance);
            }
        }
    });
    return statementResults;
}
exports.buildStatementRelevanceMap = buildStatementRelevanceMap;
/**
 * Recursive helper function for the buildStatementRelevanceMap function. This marks a statement as relevant (or not
 * relevant but applicable) in the StatementResults list. It recurses and marks dependent statements also relevant
 * unless they have already been marked as 'TRUE' for their relevance statue. This function will never be called on
 * statements that are 'NA'.
 *
 * @private
 * @param {ELM[]} elmLibraries
 * @param {LibraryDependencyInfo[]} statementDependencies - dependency map from the measure object. The thing we recurse
 *   over to find dependent statements even though it is flat, it represents a tree.
 * @param {StatementResult[]} statementResults - The list of StatementResults to mark relevance in.
 * @param {string} libraryName - The library name of the statement we are marking.
 * @param {string} statementName - The name of the statement we are marking.
 * @param {boolean} relevant - true if the statement should be marked 'TRUE', false if it should be marked 'FALSE'.
 */
function markStatementRelevant(elmLibraries, statementDependencies, statementResults, libraryName, statementName, relevant) {
    // only mark the statement if it is currently 'NA' or 'FALSE'. Otherwise it already has been marked 'TRUE'
    var statementResult = getStatementResult(libraryName, statementName, statementResults);
    if ((statementResult === null || statementResult === void 0 ? void 0 : statementResult.relevance) == Enums_1.Relevance.NA || (statementResult === null || statementResult === void 0 ? void 0 : statementResult.relevance) == Enums_1.Relevance.FALSE) {
        statementResult.relevance = relevant ? Enums_1.Relevance.TRUE : Enums_1.Relevance.FALSE;
        // grab the dependency info for this statement
        var libraryInfo = statementDependencies.find(function (lib) { return lib.libraryId === libraryName; });
        var statementInfo = libraryInfo === null || libraryInfo === void 0 ? void 0 : libraryInfo.statementDependencies.find(function (stat) { return stat.statementName === statementName; });
        if (statementInfo) {
            statementInfo.statementReferences.forEach(function (dependentStatement) {
                markStatementRelevant(elmLibraries, statementDependencies, statementResults, dependentStatement.libraryId, dependentStatement.statementName, relevant);
            });
        }
    }
}
exports.markStatementRelevant = markStatementRelevant;
/**
 * Helper function to find the StatementResult object for a given statement.
 *
 * @param {string} libraryName - Name of libary.
 * @param {string} statementName - Name of statement.
 * @param {StatementResult[]} statementResults - List of statement results to find the result in.
 * @returns {StatementResult|undefined} The statement results if found.
 */
function getStatementResult(libraryName, statementName, statementResults) {
    return statementResults.find(function (result) {
        return result.libraryName == libraryName && result.statementName == statementName;
    });
}
exports.getStatementResult = getStatementResult;
/**
 * Fills out the results attributes for the statements and builds clause results.
 *
 * The StatementResult `final` field indicates the result for the statement taking into account the statement
 * relevance in determining the result. The StatementResult also has the properties, 'raw', 'final' and 'pretty'. 'raw'
 * is the raw result from the execution engine for that statement. 'final' is the final result that takes into account
 * the relevance in this calculation. 'pretty' is a human readable description of the result that is only generated if
 * doPretty is true. The value of 'final' will be one of the following strings/enums: 'NA', 'UNHIT', 'TRUE', 'FALSE'.
 *
 * Here's what they mean:
 *
 * 'NA' - Not applicable. This statement is not relevant to any population calculation in this population_set. Common
 *   for unused library statements or statements only used for other population sets.
 *   !!!IMPORTANT NOTE!!! All define function statements are marked 'NA' since we don't have a strategy for
 *        highlighting or coverage when it comes to functions.
 *
 * 'UNHIT' - This statement wasn't hit. This is most likely because the statement was not relevant to population
 *     calculation for this patient. i.e. 'FALSE' in the the `statement_relevance` map.
 *
 * 'TRUE' - This statement is relevant and has a truthy result.
 *
 * 'FALSE' - This statement is relevant and has a falsey result.
 *
 * The ClauseResult list is similar to the StatementResult list but it indicates the results for each clause.
 * The same 'raw' and 'final' fields are set, but for the the clause itself.
 *
 * This function relies very heavily on the StatementResult `relevance` field to determine the final results.
 *
 * @public
 * @param {fhir4.Measure} measure - The measure.
 * @param {ELM[]} elmLibraries - List of all ELM Library JSONs.
 * @param {any} rawClauseResults - The raw clause results from the calculation engine.
 * @param {StatementResult[]} statementResults - The `statement_relevance` map. Used to determine if they were hit or not.
 * @param {boolean} doPretty - If true, also generate pretty versions of result.
 * @returns {object} Object with the statement_results and clause_results structures, keyed as such.
 */
function buildStatementAndClauseResults(measure, elmLibraries, rawClauseResults, statementResults, doPretty, includeClauseResults) {
    if (doPretty == null) {
        doPretty = false;
    }
    var clauseResults = [];
    // Iterate over statement results that already had relevance information populated and fill out
    // raw and final result information.
    statementResults.forEach(function (statementResult) {
        var elmLibrary = elmLibraries.find(function (library) { return library.library.identifier.id == statementResult.libraryName; });
        // If the elm for the library is not found skip. Should never happen.
        if (!elmLibrary) {
            return;
        }
        var rawStatementResult = findResultForStatementClause(elmLibrary, statementResult.statementName, rawClauseResults);
        statementResult.raw = rawStatementResult;
        //TODO: determine how SDEs should be handled
        //const isSDE = ClauseResultsHelpers.isSupplementalDataElementStatement(measure.supplementalData, statement_name);
        if ( /*(!measure.calculate_sdes && isSDE) || */statementResult.relevance == Enums_1.Relevance.NA) {
            statementResult.final = Enums_1.FinalResult.NA;
            if (doPretty) {
                statementResult.pretty = 'NA';
            }
        }
        else if (statementResult.relevance == Enums_1.Relevance.FALSE || rawClauseResults[statementResult.libraryName] == null) {
            statementResult.final = Enums_1.FinalResult.UNHIT;
            // even if the statement wasn't hit, we want the pretty result to just
            // be FUNCTION for functions
            if (doPretty) {
                if (ClauseResultsHelpers.isStatementFunction(elmLibrary, statementResult.statementName)) {
                    statementResult.pretty = 'FUNCTION';
                }
                else {
                    statementResult.pretty = 'UNHIT';
                }
            }
        }
        else if (doesResultPass(rawStatementResult)) {
            statementResult.final = Enums_1.FinalResult.TRUE;
            if (doPretty) {
                statementResult.pretty = prettyResult(rawStatementResult);
            }
        }
        else {
            statementResult.final = Enums_1.FinalResult.FALSE;
            if (rawStatementResult instanceof Array && rawStatementResult.length === 0) {
                // Special case, handle empty array.
                if (doPretty) {
                    statementResult.pretty = 'FALSE ([])';
                }
            }
            else if (ClauseResultsHelpers.isStatementFunction(elmLibrary, statementResult.statementName)) {
                if (doPretty) {
                    statementResult.pretty = 'FUNCTION';
                }
            }
            else if (doPretty) {
                statementResult.pretty = "FALSE (" + rawStatementResult + ")";
            }
        }
        if (includeClauseResults) {
            // create clause results for all localIds in this statement
            var localIds = ClauseResultsHelpers.findAllLocalIdsInStatementByName(elmLibrary, statementResult.statementName);
            for (var localId in localIds) {
                var clause = localIds[localId];
                var rawClauseResult = rawClauseResults[statementResult.libraryName] != null
                    ? rawClauseResults[statementResult.libraryName][clause.sourceLocalId != null ? clause.sourceLocalId : localId]
                    : undefined;
                var clauseResult = {
                    // if this clause is an alias or a usage of alias it will get the raw result from the sourceLocalId.
                    raw: rawClauseResult,
                    statementName: statementResult.statementName,
                    libraryName: statementResult.libraryName,
                    localId: localId,
                    final: Enums_1.FinalResult.NA
                };
                clauseResult.final = setFinalResults({
                    rawClauseResults: rawClauseResults,
                    statementRelevance: statementResult.relevance,
                    libraryName: statementResult.libraryName,
                    clause: clause,
                    rawResult: clauseResult.raw
                });
                clauseResults.push(clauseResult);
            }
        }
    });
    return clauseResults;
}
exports.buildStatementAndClauseResults = buildStatementAndClauseResults;
/**
 * Generates a pretty human readable representation of a result.
 *
 * @param {(Array|object|boolean|???)} result - The result from the calculation engine.
 * @param {number|undefined} indentLevel - For nested objects, the indentLevel indicates how far to indent.
 *                                Note that 1 is the base because Array(1).join ' ' returns ''.
 * @param {number|undefined} keyIndent - Indent count used for key indentation.
 * @returns {String} a pretty version of the given result
 */
function prettyResult(result, indentLevel, keyIndent) {
    // TODO: Sort out a better way to have a friendly system display for FHIR codes.
    //   This will need to be replaced with a canonical URL to code system name map.
    var nameOidHash = {
        '2.16.840.1.113883.6.96': 'SNOMEDCT',
        '2.16.840.1.113883.6.1': 'LOINC',
        '2.16.840.1.113883.6.238': 'CDCREC',
        '2.16.840.1.113883.6.14': 'HCP',
        '2.16.840.1.113883.6.285': 'HCPCS',
        '2.16.840.1.113883.6.103': 'ICD-9-CM',
        '2.16.840.1.113883.6.104': 'ICD-9-PCS',
        '2.16.840.1.113883.6.90': 'ICD-10-CM',
        '2.16.840.1.113883.6.4': 'ICD-10-PCS',
        '2.16.840.1.113883.6.88': 'RxNorm',
        '2.16.840.1.113883.3.221.5': 'Source of Payment Typology',
        '2.16.840.1.113883.6.12': 'CPT',
        '2.16.840.1.113883.5.1': 'AdministrativeGender',
        '2.16.840.1.113883.4.642.3.921': 'HL7 Relationship Code',
        '2.16.840.1.113883.5.2': 'HL7 Marital Status',
        '2.16.840.1.113883.12.292': 'CVX',
        '2.16.840.1.113883.5.83': 'HITSP C80 Observation Status',
        '2.16.840.1.113883.3.26.1.1': 'NCI Thesaurus',
        '2.16.840.1.113883.3.88.12.80.20': 'FDA',
        '2.16.840.1.113883.4.9': 'UNII',
        '2.16.840.1.113883.6.69': 'NDC',
        '2.16.840.1.113883.5.14': 'HL7 ActStatus',
        '2.16.840.1.113883.6.259': 'HL7 Healthcare Service Location',
        '2.16.840.1.113883.12.112': 'DischargeDisposition',
        '2.16.840.1.113883.5.4': 'HL7 Act Code',
        '2.16.840.1.113883.6.177': 'NLM MeSH',
        '2.16.840.1.113883.5.1076': 'Religious Affiliation',
        '2.16.840.1.113883.1.11.19717': 'HL7 ActNoImmunicationReason',
        '2.16.840.1.113883.3.88.12.80.33': 'NUBC',
        '2.16.840.1.113883.1.11.78': 'HL7 Observation Interpretation',
        '2.16.840.1.113883.6.13': 'CDT',
        '2.16.840.1.113883.18.2': 'AdministrativeSex'
    };
    var prettyResultReturn;
    if (indentLevel == null) {
        indentLevel = 1;
    }
    if (keyIndent == null) {
        keyIndent = 1;
    }
    var keyIndentation = Array(keyIndent).join(' ');
    var currentIndentation = Array(indentLevel).join(' ');
    if (result instanceof cql_execution_1.DateTime) {
        return moment_1.default.utc(result.toString()).format('MM/DD/YYYY h:mm A');
    }
    else if (result instanceof cql_execution_1.Interval) {
        return "INTERVAL: " + prettyResult(result.low) + " - " + prettyResult(result.high);
    }
    else if (result instanceof cql_execution_1.Code) {
        // TODO: Sort out a better way to have a friendly system display for FHIR codes
        return "CODE: " + result.system + " " + result.code;
    }
    else if (result instanceof cql_execution_1.Quantity) {
        var quantityResult = "QUANTITY: " + result.value;
        if (result.unit) {
            quantityResult += " " + result.unit;
        }
        return quantityResult;
    }
    else if (result && typeof result._type === 'string' && result._type.includes('QDM::')) {
        // If there isn't a description, use the type name as a fallback.  This mirrors the frontend where we do
        // result.constructor.name.
        var description = result.description ? result.description + "\n" : result._type.replace('QDM::', '') + "\n";
        var startDateTime = null;
        var endDateTime = null;
        var startTimeString = '';
        var endTimeString = '';
        // Start time of data element is start of relevant period, if data element does not have relevant period, use authorDatetime
        if (result.relevantPeriod) {
            if (result.relevantPeriod.low) {
                startDateTime = result.relevantPeriod.low;
            }
            if (result.relevantPeriod.high) {
                endDateTime = result.relevantPeriod.high;
            }
        }
        else if (result.prevalencePeriod) {
            if (result.prevalencePeriod.low) {
                startDateTime = result.prevalencePeriod.low;
            }
            if (result.prevalencePeriod.high) {
                endDateTime = result.prevalencePeriod.high;
            }
        }
        else if (result.authorDatetime) {
            // TODO: start result string will need to be updated to AUTHORED once bonnie frontend
            // updates its pretty printer to do so.
            startDateTime = result.authorDatetime;
        }
        if (startDateTime) {
            startTimeString = "START: " + moment_1.default.utc(startDateTime.toString()).format('MM/DD/YYYY h:mm A') + "\n";
        }
        // If endTime is the infinity dateTime, clear it out because we do not want to export it
        if (endDateTime && endDateTime.year !== 9999) {
            endTimeString = "STOP: " + moment_1.default.utc(endDateTime.toString()).format('MM/DD/YYYY h:mm A') + "\n";
        }
        var system = result.dataElementCodes[0].system;
        // TODO: Sort out a better way to have a friendly system display for FHIR codes
        var codeDisplay = result.dataElementCodes && result.dataElementCodes[0] ? "CODE: " + system + " " + result.dataElementCodes[0].code : '';
        // Add indentation
        var returnString = "" + description + startTimeString + endTimeString + codeDisplay;
        return returnString.replace(/\n/g, "\n" + currentIndentation + keyIndentation);
    }
    else if (result instanceof String || typeof result === 'string') {
        return "\"" + result + "\"";
    }
    else if (result instanceof Array) {
        prettyResultReturn = result.map(function (value) { return prettyResult(value, indentLevel, keyIndent); });
        return "[" + prettyResultReturn.join(",\n" + currentIndentation + keyIndentation) + "]";
    }
    else if (result instanceof Object) {
        // if the object has it's own custom toString method, use that instead
        if (typeof result.toString === 'function' && result.toString !== Object.prototype.toString) {
            return result.toString();
        }
        prettyResultReturn = '{\n';
        var baseIndentation = Array(3).join(' ');
        // TODO: Update the this function to better handle FHIR objects instead of QDM objects.
        var sortedKeys = Object.keys(result)
            .sort()
            .filter(function (key) { return key !== '_type' && key !== 'qdmVersion'; });
        for (var _i = 0, sortedKeys_1 = sortedKeys; _i < sortedKeys_1.length; _i++) {
            var key = sortedKeys_1[_i];
            // add 2 spaces per indent
            var value = result[key];
            var nextIndentLevel = indentLevel + 2;
            // key length + ': '
            keyIndent = key.length + 3;
            prettyResultReturn = prettyResultReturn.concat("" + baseIndentation + currentIndentation + key + ": " + prettyResult(value, nextIndentLevel, keyIndent));
            // append commas if it isn't the last key
            if (key === sortedKeys[sortedKeys.length - 1]) {
                prettyResultReturn += '\n';
            }
            else {
                prettyResultReturn += ',\n';
            }
        }
        prettyResultReturn += currentIndentation + "}";
        return prettyResultReturn;
    }
    if (result) {
        return JSON.stringify(result, null, 2);
    }
    return 'null';
}
exports.prettyResult = prettyResult;
/**
 * Determines the final result (for coloring and coverage) for a clause. The result fills the 'final' property for the
 * clause result. Look at the comments for buildStatementAndClauseResults to get a description of what each of the
 * string results of this function are.
 * @private
 * @param {object} rawClauseResults - The raw clause results from the calculation engine.
 * @param {object} statementRelevance - The statement relevance map.
 * @param {object} statement_name - The name of the statement the clause is in
 * @param {object} library_name - The name of the libarary the clause is in
 * @param {object} localId - The localId of the current clause
 * @param {object} clause - The clause we are getting the final result of
 * @param {Array|Object|Interval|??} rawResult - The raw result from the calculation engine.
 * @returns {string} The final result for the clause.
 */
function setFinalResults(params) {
    var finalResult = Enums_1.FinalResult.FALSE;
    if (params.clause.isUnsupported != null) {
        finalResult = Enums_1.FinalResult.NA;
    }
    else if (params.statementRelevance == Enums_1.Relevance.NA) {
        finalResult = Enums_1.FinalResult.NA;
    }
    else if (params.statementRelevance == Enums_1.Relevance.FALSE || params.rawClauseResults[params.libraryName] == null) {
        finalResult = Enums_1.FinalResult.UNHIT;
    }
    else if (doesResultPass(params.rawResult)) {
        finalResult = Enums_1.FinalResult.TRUE;
    }
    return finalResult;
}
exports.setFinalResults = setFinalResults;
/**
 * Finds the clause localId for a statement and gets the raw result for it from the raw clause results.
 * @private
 * @param {ELM} library - The library
 * @param {string} statement - The statement
 * @param {cql.LocalIdResults} rawClauseResults - The raw clause results from the engine.
 * @returns {(Array|object|Interval|??)} The raw result from the calculation engine for the given statement.
 */
function findResultForStatementClause(elm, statementName, rawClauseResults) {
    var elmStatement = elm.library.statements.def.find(function (def) { return def.name === statementName; });
    var libraryName = elm.library.identifier.id;
    var localId = elmStatement === null || elmStatement === void 0 ? void 0 : elmStatement.localId;
    if (localId != undefined) {
        return rawClauseResults[libraryName] != null ? rawClauseResults[libraryName][localId] : undefined;
    }
    else {
        return undefined;
    }
}
exports.findResultForStatementClause = findResultForStatementClause;
/**
 * Determines if a result (for a statement or clause) from the execution engine is a pass or fail.
 * @private
 * @param {(Array|object|boolean|???)} result - The result from the calculation engine.
 * @returns {boolean} true or false
 */
function doesResultPass(result) {
    if (result === true) {
        // Specifically a boolean true
        return true;
    }
    else if (result === false) {
        // Specifically a boolean false
        return false;
    }
    else if (Array.isArray(result)) {
        // Check if result is an array
        if (result.length === 0) {
            // Result is true if the array is not empty
            return false;
        }
        else if (result.length === 1 && result[0] === null) {
            // But if the array has one element that is null. Then we should make it red.
            return false;
        }
        return true;
    }
    else if (result instanceof cql_execution_1.Interval) {
        // make it green if and Interval is returned
        return true;
        // Return false if an empty CQL Code is the result
    }
    else if (result instanceof cql_execution_1.Code && result.code == null) {
        return false;
    }
    else if (result === null || result === undefined) {
        // Specifically no result
        return false;
    }
    return true;
}
exports.doesResultPass = doesResultPass;
/**
 * Iterate over episode results, call _buildPopulationRelevanceMap for each result then OR population relevances
 * together so that a patient level population relevance set can be made.
 *
 * @private
 * @param {EpisodeResults[]} episodeResultsSet - Results for each episode
 * @returns {PopulationResult[]} List denoting if population calculation was considered/relevant in any episode
 */
function buildPopulationRelevanceForAllEpisodes(populationGroup, episodeResultsSet) {
    var _a;
    var masterRelevanceResults = ((_a = populationGroup.population) === null || _a === void 0 ? void 0 : _a.map(function (population) {
        return {
            populationType: MeasureBundleHelpers.codeableConceptToPopulationType(population.code),
            result: false
        };
    })) || []; // Should not end up becoming an empty list.
    episodeResultsSet.forEach(function (episodeResults) {
        var episodeRelevance = buildPopulationRelevanceMap(episodeResults.populationResults);
        masterRelevanceResults.forEach(function (masterPopResults) {
            // find relevance in episode and if true, make master relevance true, only if not already true
            if (masterPopResults.result === false) {
                if (getResult(masterPopResults.populationType, episodeRelevance) === true) {
                    masterPopResults.result = true;
                }
            }
        });
    });
    return masterRelevanceResults;
}
exports.buildPopulationRelevanceForAllEpisodes = buildPopulationRelevanceForAllEpisodes;
/**
 * Builds the relevance results for each population. This creates the `populationRelevance` list that is put on
 * DetailedPopulationGroupResult
 *
 * For each population in the group it is set to true if the population was relevant/considered, false if
 * NOT relevant/considered. This is used later to determine which define statements are relevant in the calculation.
 *
 * For example: If they aren't in the IPP then they are not going to be considered for any other population and all other
 * populations will be marked NOT relevant.
 *
 * This function is extremely verbose because this is an important and confusing calculation to make. The verbosity
 * was kept to make it more maintainable.
 *
 * @param {PopulationResult[]} result - The population results list for the population results.
 * @returns {PopulationResult[]} The population relevance set.
 */
function buildPopulationRelevanceMap(results) {
    // Create initial results starting with all true to create the basis for relevance.
    var relevantResults = results.map(function (result) {
        return {
            populationType: result.populationType,
            result: true
        };
    });
    // If IPP is false then everything else is not calculated
    if (getResult(Enums_1.PopulationType.IPP, results) === false) {
        if (hasResult(Enums_1.PopulationType.NUMER, relevantResults)) {
            setResult(Enums_1.PopulationType.NUMER, false, relevantResults);
        }
        if (hasResult(Enums_1.PopulationType.NUMEX, relevantResults)) {
            setResult(Enums_1.PopulationType.NUMEX, false, relevantResults);
        }
        if (hasResult(Enums_1.PopulationType.DENOM, relevantResults)) {
            setResult(Enums_1.PopulationType.DENOM, false, relevantResults);
        }
        if (hasResult(Enums_1.PopulationType.DENEX, relevantResults)) {
            setResult(Enums_1.PopulationType.DENEX, false, relevantResults);
        }
        if (hasResult(Enums_1.PopulationType.DENEXCEP, relevantResults)) {
            setResult(Enums_1.PopulationType.DENEXCEP, false, relevantResults);
        }
        if (hasResult(Enums_1.PopulationType.MSRPOPL, relevantResults)) {
            setResult(Enums_1.PopulationType.MSRPOPL, false, relevantResults);
        }
        if (hasResult(Enums_1.PopulationType.MSRPOPLEX, relevantResults)) {
            setResult(Enums_1.PopulationType.MSRPOPLEX, false, relevantResults);
        }
        // values is the OBSERVs
        if (hasResult(Enums_1.PopulationType.OBSERV, relevantResults)) {
            setResult(Enums_1.PopulationType.OBSERV, false, relevantResults);
        }
    }
    // If DENOM is false then DENEX, DENEXCEP, NUMER and NUMEX are not calculated
    if (hasResult(Enums_1.PopulationType.DENOM, results) && getResult(Enums_1.PopulationType.DENOM, results) === false) {
        if (hasResult(Enums_1.PopulationType.NUMER, relevantResults)) {
            setResult(Enums_1.PopulationType.NUMER, false, relevantResults);
        }
        if (hasResult(Enums_1.PopulationType.NUMEX, relevantResults)) {
            setResult(Enums_1.PopulationType.NUMEX, false, relevantResults);
        }
        if (hasResult(Enums_1.PopulationType.DENEX, relevantResults)) {
            setResult(Enums_1.PopulationType.DENEX, false, relevantResults);
        }
        if (hasResult(Enums_1.PopulationType.DENEXCEP, relevantResults)) {
            setResult(Enums_1.PopulationType.DENEXCEP, false, relevantResults);
        }
    }
    // If DENEX is truethen NUMER, NUMEX and DENEXCEP not calculated
    if (hasResult(Enums_1.PopulationType.DENEX, results) && getResult(Enums_1.PopulationType.DENEX, results) === true) {
        if (hasResult(Enums_1.PopulationType.NUMER, relevantResults)) {
            setResult(Enums_1.PopulationType.NUMER, false, relevantResults);
        }
        if (hasResult(Enums_1.PopulationType.NUMEX, relevantResults)) {
            setResult(Enums_1.PopulationType.NUMEX, false, relevantResults);
        }
        if (hasResult(Enums_1.PopulationType.DENEXCEP, relevantResults)) {
            setResult(Enums_1.PopulationType.DENEXCEP, false, relevantResults);
        }
    }
    // If NUMER is false then NUMEX is not calculated
    if (hasResult(Enums_1.PopulationType.NUMER, results) && getResult(Enums_1.PopulationType.NUMER, results) === false) {
        if (hasResult(Enums_1.PopulationType.NUMEX, relevantResults)) {
            setResult(Enums_1.PopulationType.NUMEX, false, relevantResults);
        }
    }
    // If NUMER is true then DENEXCEP is not calculated
    if (hasResult(Enums_1.PopulationType.NUMER, results) && getResult(Enums_1.PopulationType.NUMER, results) === true) {
        if (hasResult(Enums_1.PopulationType.DENEXCEP, relevantResults)) {
            setResult(Enums_1.PopulationType.DENEXCEP, false, relevantResults);
        }
    }
    // If MSRPOPL is false then OBSERVs and MSRPOPLEX are not calculateed
    if (hasResult(Enums_1.PopulationType.MSRPOPL, results) && getResult(Enums_1.PopulationType.MSRPOPL, results) === false) {
        // values is the OBSERVs
        if (hasResult(Enums_1.PopulationType.OBSERV, relevantResults)) {
            setResult(Enums_1.PopulationType.OBSERV, false, relevantResults);
        }
        if (hasResult(Enums_1.PopulationType.MSRPOPLEX, relevantResults)) {
            setResult(Enums_1.PopulationType.MSRPOPLEX, false, relevantResults);
        }
    }
    // If MSRPOPLEX is true then OBSERVs are not calculated
    if (hasResult(Enums_1.PopulationType.MSRPOPLEX, results) && getResult(Enums_1.PopulationType.MSRPOPLEX, results) === true) {
        if (hasResult(Enums_1.PopulationType.OBSERV, relevantResults)) {
            setResult(Enums_1.PopulationType.OBSERV, false, relevantResults);
        }
    }
    return relevantResults;
}
exports.buildPopulationRelevanceMap = buildPopulationRelevanceMap;
function buildPopulationGroupRelevanceMap(group, results) {
    // Episode of care measure
    if (results.episodeResults) {
        return buildPopulationRelevanceForAllEpisodes(group, results.episodeResults);
        // Normal patient based measure
    }
    else if (results.populationResults) {
        return buildPopulationRelevanceMap(results.populationResults);
    }
    else {
        // this shouldn't happen
        return [];
    }
}
exports.buildPopulationGroupRelevanceMap = buildPopulationGroupRelevanceMap;
function hasResult(populationType, results) {
    return results.find(function (result) { return result.populationType == populationType; }) !== undefined;
}
exports.hasResult = hasResult;
// If the given population result is in the given result list, return the result
function getResult(populationType, results) {
    var _a;
    if (((_a = results.find(function (result) { return result.populationType == populationType; })) === null || _a === void 0 ? void 0 : _a.result) == true) {
        return true;
    }
    return false;
}
exports.getResult = getResult;
// If the given value is in the given populationSet, set the result to the new result
function setResult(populationType, newResult, results) {
    var popResult = results.find(function (result) { return result.populationType == populationType; });
    if (popResult) {
        popResult.result = newResult;
    }
}
exports.setResult = setResult;
// create a result for the given population type and result or update the existing value to true if newResult is true
function createOrSetResult(populationType, newResult, results) {
    var popResult = results.find(function (result) { return result.populationType == populationType; });
    if (popResult) {
        if (newResult === true) {
            popResult.result = true;
        }
    }
    else {
        results.push({
            populationType: populationType,
            result: newResult
        });
    }
}
exports.createOrSetResult = createOrSetResult;
// Get raw results of matching SDE expressions for each SDE in the Measure
function getSDEValues(measure, statementResults) {
    var results = [];
    if (measure.supplementalData) {
        measure.supplementalData.forEach(function (sde) {
            var _a, _b;
            if ((_a = sde.criteria) === null || _a === void 0 ? void 0 : _a.expression) {
                var expression = sde.criteria.expression;
                var result = statementResults[expression];
                results.push({
                    name: ((_b = sde.code) === null || _b === void 0 ? void 0 : _b.text) || expression,
                    rawResult: result,
                    pretty: prettyResult(result)
                });
            }
        });
    }
    return results;
}
exports.getSDEValues = getSDEValues;
//# sourceMappingURL=ClauseResultsBuilder.js.map